import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
    Checkbox,
    Form,
    Input,
    Tooltip,
    Select,
    Modal,
    Row,
    Col,
    Image,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import s from './s.module.less';
import PhotoList from 'components/PhotoList';
import { EMPTY_PLACEHOLDER } from 'constants/common';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { EAIEditorType } from 'types/ai';
import AiTextEditor from 'components/AI/AiTextEditor';
import UsPhoneInput from 'components/form/USPhoneInput';
import FormValidationHelper from 'utils/validation';

interface PracticeFormData {
    practiceName?: string;
    practicePhone?: string;
    practiceEmail?: string;
    practiceDesc?: string;
    practiceLogo?: string;
    notHavePhone?: boolean;
    taxIdType?: string;
    taxId?: string;
    practiceFrontUrl?: string;
}

interface IProps {
    conversationId?: string;
    providerId?: number;
    initFormValue: PracticeFormData;
    initPracticeName: string;
    enable: boolean;
    isOps?: boolean;
}

const SummaryItem = ({ label, value, fullWidth = false }: { label: string; value: React.ReactNode, fullWidth?: boolean }) => (
    <Col span={fullWidth ? 24 : 12} className={s.summaryItem}>
        <div className={s.summaryLabel}>{label}</div>
        <div className={s.summaryValue}>{value || EMPTY_PLACEHOLDER}</div>
    </Col>
);

const PracticeForm = forwardRef<any, IProps>(({
    conversationId,
    providerId,
    isOps,
    initFormValue,
    initPracticeName,
    enable,
}, ref) => {
    const [practicePhoneDisable, setPracticePhoneDisable] = useState(false);
    const [practiceForm] = Form.useForm();
    const [setting] = useSettings();
    const taxIdTypeOptions = setting[EnumFields.TAX_ID_TYPE] || [];
    const [showTaxModal, setShowTaxModal] = useState(false);

    const {
        practiceDesc,
        practiceEmail,
        practiceLogo = '',
        practiceName,
        practicePhone,
        notHavePhone,
        taxIdType,
        taxId,
    } = initFormValue || {};

    useEffect(() => {
        practiceForm.setFieldsValue({
            ...initFormValue,
            practiceName: initFormValue.practiceName ?? initPracticeName,
        });
        setPracticePhoneDisable(initFormValue.notHavePhone ?? false);
    }, [initFormValue, practiceForm]);

    useEffect(() => {
        setPracticePhoneDisable(!!notHavePhone);
    }, [notHavePhone]);

    const getFormValues = useCallback(async () => {
        try {
            await practiceForm.validateFields();
            return practiceForm.getFieldsValue();
        } catch (e) {
            console.error(e);
            throw e;
        }
    }, [initPracticeName, practiceForm, practiceName]);

    useImperativeHandle(ref, () => ({
        getFormValues,
    }));

    const handleDisablePhone = useCallback((e: CheckboxChangeEvent) => {
        practiceForm.validateFields();
        practiceForm.setFieldValue('practicePhone', '');
        setPracticePhoneDisable(e.target.checked);
    }, [practiceForm]);

    const handleHideTaxModal = useCallback(() => setShowTaxModal(false), []);
    const handleShowTaxPopup = useCallback(() => setShowTaxModal(true), []);

    const renderSummaryView = () => {
        const summaryPracticeInfo = {
            practiceName: !practiceName?.replace(/\s+/g, '')
                ? initPracticeName
                : practiceName,
            practicePhone: notHavePhone || !practicePhone?.replace(/\s+/g, '')
                ? "I don't have a practice phone number yet"
                : practicePhone,
            practiceEmail: practiceEmail || 'Not provided',
            taxIdTypeDisplay: taxIdTypeOptions.find((item) => item.value === taxIdType)?.label ||
                taxIdTypeOptions.find((item) => item.value === 'NONE')?.label ||
                'My practice doesn\'t have a Tax ID',
            practiceTaxId: taxId || 'Not provided',
            practiceDesc: practiceDesc || 'Not provided',
            practiceLogo,
        };

        return (
            <div className={s.summaryContainer}>
                <Row gutter={[24, 32]}>
                    <SummaryItem label="Practice name" value={summaryPracticeInfo.practiceName} />
                    <SummaryItem label="Practice phone number" value={summaryPracticeInfo.practicePhone} />
                    <SummaryItem label="Practice email address" value={summaryPracticeInfo.practiceEmail} />
                </Row>
                <Row gutter={[24, 32]} style={{ marginTop: '32px' }}>
                    <SummaryItem label="Practice Tax ID type" value={summaryPracticeInfo.taxIdTypeDisplay} />
                    {taxIdType && ['SSN', 'EIN'].includes(taxIdType) && (
                        <SummaryItem label="Practice Tax ID" value={summaryPracticeInfo.practiceTaxId} />
                    )}
                </Row>
                <Row gutter={[24, 32]} style={{ marginTop: '32px' }}>
                    <SummaryItem label="Practice description" value={summaryPracticeInfo.practiceDesc} fullWidth />
                </Row>
                <Row gutter={[24, 32]} style={{ marginTop: '32px' }}>
                    <Col span={24}>
                        <div className={s.summaryLabel}>Practice logo</div>
                        {summaryPracticeInfo.practiceLogo ? (
                            <Image width={150} src={summaryPracticeInfo.practiceLogo} preview={false} className={s.summaryLogo} />
                        ) : (
                            <div className={s.summaryValue}>Not provided</div>
                        )}
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <div className={s.practiceFormWrapper}>
            {!enable ? (
                renderSummaryView()
            ) : (
                <div className={s.formCard}>
                    <Form
                        form={practiceForm}
                        layout="vertical"
                        scrollToFirstError
                    >
                        <div className={s.formGridContainer}>
                            <div className={s.formGridItem}>
                                <Form.Item
                                    label={
                                        <div className={s.formLabel}>
                                            Practice name
                                            <Tooltip
                                                title="Your practice name appears on patient-facing screens and documents."
                                            >
                                                <QuestionCircleOutlined className={s.tooltipIcon} />
                                            </Tooltip>
                                        </div>
                                    }
                                    name="practiceName"
                                    rules={[{ required: true, message: 'Please enter practice name' }]}
                                >
                                    <Input placeholder={initPracticeName} maxLength={200} />
                                </Form.Item>
                                <div className={s.tipsLabel}>
                                    If unchanged, &apos;[First Name] [Last Name]&apos;s Practice&apos; will be your default practice name.
                                </div>
                            </div>

                            <div className={s.formGridItem}>
                                <Form.Item
                                    label="Practice phone number"
                                    name="practicePhone"
                                    rules={practicePhoneDisable ? [] : [
                                        FormValidationHelper.validateUSPhoneNumber('Please enter a valid 10-digit phone number'),
                                    ]}
                                >
                                    <UsPhoneInput disabled={practicePhoneDisable} maxLength={30} />
                                </Form.Item>

                                <Form.Item
                                    name="notHavePhone"
                                    valuePropName="checked"
                                    className={s.checkboxItem}
                                >
                                    <Checkbox
                                        onChange={handleDisablePhone}
                                        checked={practicePhoneDisable}
                                    >
                                        I don&apos;t have a practice phone number yet
                                    </Checkbox>
                                </Form.Item>
                            </div>
                        </div>

                        <div className={s.formGridContainer}>
                            <div className={s.formGridItem}>
                                <Form.Item
                                    label={
                                        <div className={s.formLabel}>
                                            Practice email address
                                            <Tooltip
                                                title="This can be the same as your work email."
                                            >
                                                <QuestionCircleOutlined className={s.tooltipIcon} />
                                            </Tooltip>
                                        </div>
                                    }
                                    name="practiceEmail"
                                    rules={[
                                        { type: 'email', message: 'Please enter a valid email address' },
                                    ]}
                                >
                                    <Input placeholder="Enter your practice email address" />
                                </Form.Item>
                            </div>

                            <div className={s.formGridItem}>
                                <Form.Item
                                    label={
                                        <div className={s.formLabel}>
                                            <div>
                                                Practice Tax ID type
                                                <Tooltip
                                                    title="Select the appropriate Tax ID type based on your practice registration"
                                                >
                                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                                </Tooltip>
                                            </div>
                                            <div
                                                className={s.popUpLabel}
                                                onClick={handleShowTaxPopup}
                                            >
                                                Click to know more about Tax ID
                                            </div>
                                        </div>
                                    }
                                    name="taxIdType"
                                    validateFirst
                                >
                                    <Select
                                        placeholder="Search and select your credential type"
                                        options={taxIdTypeOptions}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const type = getFieldValue('taxIdType');
                                if (['SSN', 'EIN'].includes(type)) {
                                    return (
                                        <Form.Item
                                            label="Practice Tax ID"
                                            name="taxId"
                                        >
                                            <Input placeholder="Enter your Tax ID" maxLength={200} />
                                        </Form.Item>
                                    );
                                }
                                return null;
                            }}
                        </Form.Item>

                        <Form.Item
                            label="Practice description"
                            name="practiceDesc"
                        >
                            <AiTextEditor
                                maxLength={1000}
                                isOps={isOps || undefined}
                                providerId={providerId}
                                type={EAIEditorType.PRACTICE_DESCRIPTION}
                                conversionId={conversationId}
                            />
                        </Form.Item>

                        <div className={s.formRow}>
                            <div className={s.formItemLabel}>
                                Practice logo
                            </div>
                            <div className={s.subLabel}>
                                To ensure optimal display, please upload your{' '}
                                <strong>logo with a transparent background</strong>
                            </div>
                            <Form.Item
                                name="practiceLogo"
                                valuePropName="data"
                            >
                                <PhotoList
                                    min={1}
                                    data={[practiceLogo]}
                                    enable
                                    responseType="string"
                                />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            )}

            {/* Tax ID 信息弹窗 */}
            <Modal
                title="Understanding Tax ID"
                centered
                open={showTaxModal}
                onCancel={handleHideTaxModal}
                width={1000}
                footer={null}
            >
                <div className={s.modalContent}>
                    <div className={s.list}>
                        <div className={s.listItem}>
                            <p className={s.question}>
                                Why do I need to input a Tax ID in the UniProfile?
                            </p>
                            <p className={s.answer}>
                                A Tax ID will appear on all kiwi invoices and superbills while
                                using Kiwi EHR or Klarity. The superbills is what the patient
                                submits to the insurance companies for reimbursements. The
                                insurance payer then looks up the Tax ID to verify providers
                                identity. Therefore, a Tax ID is required for generating
                                invoices and processing payments, ensuring compliance with
                                financial regulations.
                            </p>
                        </div>
                        <div className={s.listItem}>
                            <p className={s.question}>What is EIN?</p>
                            <p className={s.answer}>
                                An Employer Identification Number (EIN) is a unique identifier
                                for businesses, used for tax purposes.
                            </p>
                        </div>
                        <div className={s.listItem}>
                            <p className={s.question}>What is SSN?</p>
                            <p className={s.answer}>
                                A Social Security Number (SSN) is a personal identifier
                                assigned to U.S. citizens and some residents for social
                                security and taxation.
                            </p>
                        </div>
                        <div className={s.listItem}>
                            <p className={s.question}>
                                What is the difference between EIN and SSN?
                            </p>
                            <p className={s.answer}>
                                EIN is for businesses, while SSN is for individuals. EIN is
                                recommended for privacy and professional transactions.
                            </p>
                        </div>
                        <div className={s.listItem}>
                            <p className={s.question}>How to apply for an EIN?</p>
                            <p className={s.answer}>
                                You can apply for an EIN online through the IRS website (
                                <a href="https://www.irs.gov" target="_blank" rel="noopener noreferrer">
                                    www.irs.gov
                                </a>
                                ). Typically it takes 24-48 hours to process your request.
                            </p>
                        </div>
                        <div className={s.listItem}>
                            <p className={s.question}>
                                Why is an EIN recommended over an SSN for your Tax ID?
                            </p>
                            <p className={s.answer}>
                                We don&apos;t recommend using SSN as your Tax ID on invoices
                                because it poses a risk to your personal security. Exposing
                                your SSN, especially on documents like invoices that are
                                widely circulated, can lead to identity theft and other forms
                                of financial fraud. An EIN is a safer alternative that
                                provides the necessary tax identification for your business
                                without compromising personal information.
                            </p>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
});

export default PracticeForm;
